// src/components/desk/ListingGallery.jsx — the card gallery + its location helpers,
// extracted VERBATIM from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition,
// behavior-preserving). splitCityFromAddress is exported too — 14 other call sites
// in IntakeTab consume it from here.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { geocode } from '../../db/core.js'
import { ListingMap } from './ListingMap.jsx'
import { OVERLAY_BADGE } from './primitives.jsx'

// The address's OWN first comma segment (e.g. "თბილისი, ვაკე-საბურთალო, ..."). Used both to
// strip the city prefix off card titles and to power the City filter.
// ⚠️ This is a positional split, NOT a city detector — see cityForDisplay below before you
// treat `city` as a city. (The header note that used to live here said district_code was 0%
// populated; that is long stale — it is 99.8% now, 2026-07-27 — but the registry's city is
// a RESOLVED value and disagrees with the ad on real rows, so it is still not the display
// source. Details in cityForDisplay.)
export function splitCityFromAddress(address) {
  if (!address) return { city: null, rest: null }
  const parts = address.split(',')
  if (parts.length < 2) return { city: null, rest: address.trim() }
  return { city: parts[0].trim(), rest: parts.slice(1).join(',').trim() }
}

// The city the agency works in. Its listings are ~91% Tbilisi (27,023 of 29,693 active,
// counted 2026-07-27), which is why the card title strips it: printing one identical word on
// nine cards in ten is noise that costs the street its pixels. The other ~9% (Rustavi 489,
// Batumi 512, Bakuriani, Kutaisi, …) are exactly the cards where the agent must NOT assume,
// so those keep their city. One constant, display-only — never filter or match on it.
export const HOME_CITY = { ka: 'თბილისი', en: 'Tbilisi' }

// Lowercased registry name sets, cached per districts array — summarize() runs per card, and
// rebuilding these for a page of cards is the kind of per-row O(n) the 2026-07-27 render-perf
// pass just removed.
//   cities    — every district row's `city` value (Latin), plus the Georgian name of the row
//               that IS its own city (name_en === city, e.g. Rustavi/რუსთავი, Kutaisi/ქუთაისი).
//   districts — every row's name_ka/name_en.
// ⚠️ THE TWO OVERLAP, AND CITIES MUST WIN. A city has its own district row in this registry, so
// "ქუთაისი" and "რუსთავი" are in BOTH sets; checking districts first classified every major
// city as a bare district (caught in the browser 2026-07-27 — the city still printed, but via
// the wrong branch, so it lost its emphasis and let the district prepend ahead of it).
const registryCache = new WeakMap()
function registryNames(districts) {
  if (!Array.isArray(districts)) return null
  let sets = registryCache.get(districts)
  if (!sets) {
    sets = { cities: new Set(), districts: new Set() }
    for (const d of districts) {
      if (d?.name_ka) sets.districts.add(d.name_ka.toLowerCase())
      if (d?.name_en) sets.districts.add(d.name_en.toLowerCase())
      if (d?.city) {
        sets.cities.add(d.city.toLowerCase())
        if (d.name_en && d.name_ka && d.name_en === d.city) sets.cities.add(d.name_ka.toLowerCase())
      }
    }
    registryCache.set(districts, sets)
  }
  return sets
}

// Decides what a card should print as its city, and what is left over as the street part.
//
// Source is the ADDRESS's own first segment — what the ad itself said — not the registry's
// resolved district.city. Measured 2026-07-27 on live active listings, the registry value is
// wrong in a whole class of rows: ~100 addresses reading "ამბროლაური, …" / "ბაღდათი, …" /
// "დედოფლისწყარო, …" carry city='Tbilisi' because their pin snapped to a Tbilisi district
// (see the district-resolution model). Printing a resolved guess as if it were a fact is the
// one thing the card must not do.
//
// ⚠️ The first segment is NOT always a city: 106 active rows have address_text starting with a
// bare district ("Vake, …") and no city at all. Those must not print "Vake" as a city — and,
// separately, must not have it SILENTLY STRIPPED either, which is what the plain split does to
// them today. So a first segment that is a registry district name AND NOT a city name is handed
// back inside `rest` and reported as no city.
//
// An unrecognised segment is treated as a city rather than dropped — the registry's city column
// is Latin-only and misses Georgian forms for smaller towns (ამბროლაური, ბაღდათი, …), and
// between "print what the ad said" and "hide it because we don't have a row for it", printing
// wins. It is an observation either way; the registry is not the arbiter of what exists.
export function cityForDisplay(address, districts) {
  const { city, rest } = splitCityFromAddress(address)
  if (!city) return { city: null, rest: rest || null }
  const lc = city.toLowerCase()
  const names = registryNames(districts)
  // Cities are checked FIRST — they are district rows too (see registryNames).
  if (names && !names.cities.has(lc) && names.districts.has(lc)) {
    return { city: null, rest: address.trim() }
  }
  const isHome = lc === HOME_CITY.ka.toLowerCase() || lc === HOME_CITY.en.toLowerCase()
  return { city: isHome ? null : city, rest }
}

// One-line form for the surfaces that just want a display string (match rows, lineage header)
// rather than the city and street separately. `districts` is optional — without it the
// bare-district guard is skipped (every first segment reads as a city) and the home-city strip
// still applies.
export function addressForDisplay(address, districts) {
  const { city, rest } = cityForDisplay(address, districts)
  if (!rest) return city || null
  return city ? `${city}, ${rest}` : rest
}

function latLngToTile(lat, lng, zoom) {
  const latRad = lat * Math.PI / 180
  const n = 2 ** zoom
  const x = Math.floor((lng + 180) / 360 * n)
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n)
  return { x, y, z: zoom }
}
export function osmTileUrl(lat, lng, zoom = 15) {
  const { x, y, z } = latLngToTile(lat, lng, zoom)
  return `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`
}

// ── OsmPinPatch (owner report 2026-08-26: "why all face card pins are wrong and same?") ──────
// The old map slide fetched THE ONE TILE CONTAINING the point and drew 📍 at the box centre —
// but the point can be anywhere inside a tile, and a z15 tile spans ~900 m here. So the pin was
// wrong by up to half a tile, and IDENTICALLY wrong for every card whose flat falls in the same
// tile (the owner's screenshot: six flats of one building, six cards, one shared pin position by
// the river while the building sits 400 m north — the modal's real Leaflet told the truth and
// the card face did not). Its own comment even admitted it: "placed at the tile's visual center
// as an approximation." An approximation that is systematically identical across cards does not
// read as approximate — it reads as a fact, which is why it surfaced as a bug report, not a
// quality note.
// The fix is the same window math AreaThumb (HistoryCards) already proves: compute the point's
// global pixel position, set the window origin so the POINT sits at the box centre, and lay the
// 2–4 tiles that intersect the window. The centred emoji is then truthful by construction.
// Still no Leaflet instance — a grid of cards cannot afford one each (the reason the tile
// approach exists at all), and tiles come from the same OSM server as before.
export function OsmPinPatch({ lat, lng, zoom = 15 }) {
  const ref = useRef(null)
  const [box, setBox] = useState(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (el) setBox({ w: el.offsetWidth || 256, h: el.offsetHeight || 192 })
  }, [])
  const tiles = useMemo(() => {
    if (!box) return null
    const nPx = 256 * 2 ** zoom
    const latRad = lat * Math.PI / 180
    const cx = (lng + 180) / 360 * nPx
    const cy = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * nPx
    const ox = cx - box.w / 2, oy = cy - box.h / 2
    const out = []
    for (let tx = Math.floor(ox / 256); tx * 256 < ox + box.w; tx++)
      for (let ty = Math.floor(oy / 256); ty * 256 < oy + box.h; ty++)
        out.push({ x: tx, y: ty, left: Math.round(tx * 256 - ox), top: Math.round(ty * 256 - oy) })
    return out
  }, [lat, lng, zoom, box])
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      {tiles && tiles.map(tl => (
        <img key={`${tl.x}-${tl.y}`} src={`https://a.tile.openstreetmap.org/${zoom}/${tl.x}/${tl.y}.png`}
          alt="" loading="lazy"
          style={{ position: 'absolute', left: tl.left, top: tl.top, width: 256, height: 256, maxWidth: 'none' }} />
      ))}
    </div>
  )
}

// Approximate location for a listing with no precise lat/lng — cascades from the fullest
// available text down to just the city, since Georgian-script district names don't always
// resolve on Nominatim but the plain city name almost always does (verified live). Keyed by
// query signature and caching the in-flight PROMISE (not just the resolved value) — many cards
// share the same district/city, so without this the first render of a page full of cards would
// fire one request per card before any of them could satisfy the others from cache.
const approxLocationCache = new Map()
async function fetchApproxLocation(listing) {
  const { city, rest } = splitCityFromAddress(listing?.address)
  const candidates = [
    rest ? `${rest}, ${city || ''}, Georgia` : null,
    listing?.district ? `${listing.district}, ${city || ''}, Georgia` : null,
    city ? `${city}, Georgia` : null,
  ].filter(Boolean)
  for (const q of candidates) {
    try { const coords = await geocode(q); if (coords) return coords } catch { /* try the next, coarser query */ }
  }
  return null
}
export function getApproxLocation(listing) {
  const key = `${listing?.address || ''}|${listing?.district || ''}`
  if (!approxLocationCache.has(key)) approxLocationCache.set(key, fetchApproxLocation(listing))
  return approxLocationCache.get(key)
}

// One slide at a time, click to advance — a map is ALWAYS the last slide (never omitted): a
// precise pin if we have lat/lng, else an approximate one geocoded from the address/district/
// city (cascading above), else — only if even the city fails to resolve — a plain placeholder
// box (nothing left to fetch, an empty box is honest, not a broken image).
// A dead photo just drops out of its own slide (onError removes it from the list) rather than
// derailing the whole carousel.
// size='thumb' (default, normal card view) is a small fixed-height strip — real thumbnails
// (is_thumb-flagged, not full-res originals), so this stays small on screen too. size='large'
// (the dedicated Gallery view) fills the card as a big square.
// Optional controlled mode (lineage modal's §9 mosaic, 2026-07-23): pass `index` +
// `onIndexChange` and the parent owns the current slide. `index` accepts sentinels so the
// parent never has to know about internal dead-photo pruning: 'map' → the trailing map
// slide, a photo URL → that photo's (post-pruning) slide, a number → plain modulo index.
// All existing call sites omit both props and keep the uncontrolled behavior verbatim.
// `zoomable` is OPT-IN and deliberately not keyed off size==='large' — HistoryCards renders a
// large gallery too, and that is a card face, not an inspection surface.
export function ListingGallery({ listing, size = 'thumb', index: extIndex, onIndexChange, zoomable = false }) {
  const [innerIndex, setInnerIndex] = useState(0)
  // Click-to-zoom, eBay-style (owner 2026-08-17, chosen over an Amazon hover lens).
  // ⚠️ IT ZOOMS WHAT THE ACTIVE SET HOLDS AND NOTHING SMARTER — the owner picked that over
  // "always open the largest file". The consequence is worth knowing before someone calls it a
  // bug: on ss.ge and myhome the CLEAN rendition is the portal's thumbnail (188,864 of 189,598
  // ss.ge urls and 236,029 of 236,221 myhome urls are *_thumb), so zooming the default tab on
  // those two portals magnifies a thumbnail and gets blur. Only korter serves full-size clean.
  // The full-size copy on ss/myhome is the WATERMARKED tab; switching to it is how you look
  // closely. Do not silently swap the source behind the agent — that was the option not taken.
  const [zoom, setZoom] = useState(false)
  // 🔴 ORIGIN IS NUMBERS NOW, not a formatted string, because dragging needs to ADD to it and
  // you cannot add to "50% 50%". The string is built at the style site instead.
  const [origin, setOrigin] = useState({ x: 50, y: 50 })
  // ── DRAG-TO-PAN WHEN ZOOMED (owner on mobile 2026-09-10: "when you press zoom image let me
  // drag and move around") ──────────────────────────────────────────────────────────────────
  // The pan has always been `onMouseMove` — an eBay-style hover lens, which the owner chose in
  // 2026-08-17 and which is right for a mouse. A phone has no hover: once zoomed you were pinned
  // to whatever origin was last set (50% 50% on a fresh open), so the zoom showed the middle of
  // the photo and nothing else. The feature was half-built for touch and nobody could see that
  // from the code, because on a desktop it works perfectly.
  // Pointer Events cover mouse, touch and pen in ONE path, so this is not a touch special case.
  const dragRef = useRef(null)      // { px, py, ox, oy, moved } while a drag is live
  const clickBlockRef = useRef(false)   // a drag must not also toggle zoom off on pointerup
  const controlled = extIndex != null
  const index = controlled ? extIndex : innerIndex
  const [approxCoords, setApproxCoords] = useState(null)
  const hasPrecise = listing?.lat != null && listing?.lng != null
  const [deadUrls, setDeadUrls] = useState(() => new Set())
  const images = (listing?.images || []).filter(url => !deadUrls.has(url))
  const slideCount = images.length + 1   // +1 for the trailing map slide
  const i = index === 'map' ? images.length
    : typeof index === 'string' ? Math.max(images.indexOf(index), 0)
    : index % slideCount
  const onMap = i === images.length
  const canZoom = zoomable && !onMap && images.length > 0
  // ⚠️ FETCHED ON THE MAP SLIDE, NOT ON MOUNT — measured 2026-08-12 in the live Desk, and the
  // mount form was the single biggest cost of changing a filter. One district click mounted a
  // page of leads cards and fired **48 geocodes**; the server serializes that queue at ~1/sec,
  // so the median call took **34s** and the slowest **63s**, and every other Desk request
  // (cooperations, listing-terms-batch, and lineage by-property at 27.7s) sat behind them.
  // Only **7** map tiles were ever rendered: 41 of the 48 answers were for a slide nobody had
  // opened. The map is the LAST slide of the carousel, so a card with photos now costs nothing
  // until someone clicks to it; a card whose ONLY slide is the map (no photos) still resolves
  // immediately — those 7 tiles are exactly that case, and they keep their behaviour.
  // `size === 'large'` (the modal hero) stays eager: one card opened deliberately, not a feed.
  //
  // ⚠️ AND IT MUST ALSO BE ON SCREEN — `onMap` ALONE DID NOT WORK, measured on the deploy.
  // Thumbnails arrive in their OWN later fetch (`listing-thumbs`), so on first render every
  // card has `images: []`, which makes the map the only slide and `onMap` true for ALL of them.
  // The gate fired for 48 cards exactly as before. Viewport is the honest second condition: an
  // approximate pin is wanted when someone can actually SEE the box it goes in. A card that
  // scrolls into view later gets its own fetch then, which is the point.
  // 📌 This is the read-time half. The durable fix is to stop deriving an approximate coordinate
  // per client per render at all — resolve it once, upstream, beside `pin_precision`.
  const boxRef = useRef(null)
  const [inView, setInView] = useState(size === 'large')
  useEffect(() => {
    if (size === 'large' || typeof IntersectionObserver !== 'function') { setInView(true); return }
    const el = boxRef.current
    if (!el) return
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) setInView(true) },
      { rootMargin: '200px' })   // 200px of lead-in so a scroll does not land on an empty box
    io.observe(el)
    return () => io.disconnect()
  }, [size])
  const wantsLocation = size === 'large' || (onMap && inView)
  useEffect(() => {
    if (!wantsLocation || hasPrecise || (!listing?.address && !listing?.district)) return
    let cancelled = false
    getApproxLocation(listing).then(c => { if (!cancelled) setApproxCoords(c) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.address, listing?.district, hasPrecise, wantsLocation])
  // A different PHOTO drops zoom, a different RENDITION keeps it — and the difference is the
  // useful part, not an accident of the deps. Moving to another photo while magnified would
  // open it at the previous photo's focal point, which is disorienting. Switching tabs is the
  // opposite case: it is the SAME photo in another rendition at the same index, so holding the
  // magnified region is exactly the comparison this surface exists for — zoom into a corner of
  // the clean copy, flip to the watermarked one, and the same corner is under your eye.
  // ⚠️ Verified in a browser (2026-08-17), because the deps do not say this on their own:
  // a tab switch leaves both `i` and `images.length` unchanged, so the effect does not re-run.
  // If either ever becomes part of the set identity, this behaviour flips silently.
  useEffect(() => { setZoom(false); setOrigin({ x: 50, y: 50 }) }, [i, images.length])
  const setIndex = fn => {
    const next = (typeof fn === 'function' ? fn(i) : fn) % slideCount
    if (controlled) onIndexChange?.(next); else setInnerIndex(next)
  }
  // 4:3 (was square) since the 2026-07-06 three-column restructure — the middle column is
  // narrower with two sidebars, so gallery cards shed vertical bulk to keep more rows visible.
  // 'card' (2026-07-17 eBay-style redesign): the normal card view's left-column image —
  // noticeably bigger than the old 'thumb' strip (which remains for dense/legacy spots),
  // sized to span the title/price/facts block beside it.
  // 'card' — width fixed at w-56 (224px), height FILLS the card row (owner request 2026-07-30,
  // after three sizing rounds: 144×112 → 176×144 → 224×176 → 224×fill).
  //
  // ⚠️ Filling was tried once before this and looked letterboxed, but the cause was NOT the
  // stretch — it was the img using `h-full` (a percentage height that does not resolve against
  // a flex-stretched parent), so the photo sat at intrinsic height with dark bg-muted banding
  // it. The img is now pinned with absolute inset-0 (see below) and genuinely fills.
  // The other half of the earlier ugliness was width: at 144px a full-height box was a narrow
  // vertical slice of the photo. At 224px the crop is close to square and reads normally.
  // ⚠️ That objection is about a STRETCHED box and does not carry to the compact rung added
  // 2026-08-05, where the photo is 112px at 4:3 and top-aligned — an ordinary list thumbnail,
  // not a slice. The 176px floor moved with the stretch, into index.css §1's wide rung, since
  // a floor is only meaningful where the box stretches.
  // 'card' is the beside-the-text form. ⚠️ w-56 is a FIXED 224px, which left the facts column
  // ~100px wide — the address wrapped to one word a line (design 2c names the fix: "photo on
  // top, one column under it"). That switch used to be `sm:`, i.e. keyed to the WINDOW, and
  // the complaint above was written about a 375px phone — but a 340px card in a grid column
  // has exactly the same 100px facts column on a 1600px monitor, and took the wide form
  // anyway. It now flips with the card (`.desk-card-photo`, index.css §1), in lockstep with
  // HistoryItem's `.desk-card-row`. Width, aspect, alignment AND the 176px height floor all
  // live there, because each of them differs per rung — the floor in particular is meaningful
  // only where the photo stretches, and on the compact rung it would force a 112px thumb to
  // 176px tall. Hence no size utilities here at all: one class, three rungs, one place.
  // ⚠️ Needs a `.desk-cq` ancestor to size against. HistoryItem's card root is one and so is
  // LineageModal's skeleton row — a THIRD consumer must declare one, or the photo silently
  // renders at the compact width everywhere (an unmatched @container matches nothing; it does
  // not fall back).
  const sizeClass = size === 'large' ? 'w-full aspect-[4/3]' : size === 'card' ? 'desk-card-photo' : 'w-24 h-16'
  const pinTextClass = size === 'large' ? 'text-3xl' : size === 'card' ? 'text-2xl' : 'text-lg'
  const coords = hasPrecise ? { lat: listing.lat, lng: listing.lng } : approxCoords

  return (
    /* no mb-2 on 'card': that margin separates a STACKED image from text beneath it, but the
       card form sits beside the text — there it just reopens a gap under a box meant to fill. */
    <div ref={boxRef} className={`relative ${sizeClass} rounded ${size === 'card' ? '' : 'mb-2'} overflow-hidden bg-muted group shrink-0 select-none
        ${canZoom ? (zoom ? 'cursor-zoom-out' : 'cursor-zoom-in') : 'cursor-pointer'}
        ${zoom ? 'touch-none' : ''}`}
      onClick={e => {
        e.stopPropagation()
        // ⚠️ A PAN IS NOT A CLICK. Without this, every drag ended by toggling zoom back off —
        // you would move one finger-width and the photo would snap shut.
        if (clickBlockRef.current) { clickBlockRef.current = false; return }
        if (canZoom) setZoom(z => !z); else setIndex(n => (n + 1) % slideCount)
      }}
      onPointerDown={e => {
        if (!canZoom || !zoom) return
        const r = e.currentTarget.getBoundingClientRect()
        if (!r.width || !r.height) return
        dragRef.current = { px: e.clientX, py: e.clientY, ox: origin.x, oy: origin.y, moved: false }
        // setPointerCapture: the finger routinely leaves the photo mid-drag, and without capture
        // the move events stop arriving there and the pan freezes half way.
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* not fatal */ }
      }}
      onPointerMove={e => {
        const d = dragRef.current
        if (!d) return
        const r = e.currentTarget.getBoundingClientRect()
        if (!r.width || !r.height) return
        const dx = (e.clientX - d.px) / r.width * 100
        const dy = (e.clientY - d.py) / r.height * 100
        // ⚠️ MINUS, and it is the whole feel of the thing: dragging RIGHT must bring the LEFT of
        // the photo into view, which means the transform-origin moves the other way. Plus here
        // reads as the image running away from your finger.
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) d.moved = true
        setOrigin({ x: Math.min(Math.max(d.ox - dx, 0), 100), y: Math.min(Math.max(d.oy - dy, 0), 100) })
      }}
      onPointerUp={e => {
        const d = dragRef.current
        dragRef.current = null
        try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* already gone */ }
        // Only a real pan blocks the click. A plain tap while zoomed must still zoom back out.
        if (d?.moved) clickBlockRef.current = true
      }}
      onPointerCancel={() => { dragRef.current = null }}
      onMouseMove={e => {
        // The desktop hover lens, unchanged — EXCEPT it yields while a drag is live, or the two
        // would fight over the same origin and the photo would jitter under the cursor.
        if (!canZoom || !zoom || dragRef.current) return
        const r = e.currentTarget.getBoundingClientRect()
        if (!r.width || !r.height) return
        const x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1) * 100
        const y = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1) * 100
        setOrigin({ x, y })
      }}
      onMouseDown={e => e.preventDefault()}
      title={canZoom ? t(zoom ? 'desk_gallery_zoom_out' : 'desk_gallery_zoom_in') : t('desk_entry_click_see_more')}>
      {!onMap ? (
        /* ⚠️ absolute inset-0, NOT just w-full h-full. `h-full` is height:100%, and a percentage
           height does not reliably resolve when the parent's height comes from FLEX STRETCH
           rather than an explicit value — the img then falls back to its intrinsic height and
           the dark bg-muted shows above and below it. That is exactly the black banding the
           first fill attempt produced on 2026-07-30. inset-0 pins all four edges, so the box is
           filled however its height was arrived at. w-full h-full stays as a belt-and-braces
           for the non-stretched sizes. */
        <img src={images[i]} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-150"
          style={zoom ? { transform: 'scale(2.6)', transformOrigin: `${origin.x}% ${origin.y}%` } : undefined}
          onError={() => setDeadUrls(s => new Set(s).add(images[i]))} />
      ) : coords ? (
        size === 'large' ? (
          // Interactive map (owner 2026-07-23: static tile "is like image — I want to zoom").
          // LARGE (modal hero) only — cards keep the cheap static tile below. Map clicks
          // don't advance the carousel (drag/zoom would misfire), so a → overlay button
          // is the way onward; z-[400] floats it above leaflet's panes.
          <>
            <ListingMap lat={coords.lat} lng={coords.lng} precise={hasPrecise} />
            {/* Vertically CENTERED on the right edge (owner ruling 2026-07-25), not the top
                corner: this is the only way off the map slide (map clicks are swallowed so
                drag/zoom don't misfire), so it has to read as a carousel "next" control —
                and the top-right corner is where every other card overlay already lives (the
                ♥ favourite, leaflet's own +/− on the left). It was competing with the heart
                for the same corner while looking like a close button. */}
            {slideCount > 1 && (
              <button type="button"
                className="absolute top-1/2 -translate-y-1/2 right-1 z-[400] bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm"
                onClick={e => { e.stopPropagation(); setIndex(n => (n + 1) % slideCount) }}>→</button>
            )}
          </>
        ) : (
        // The window is CENTRED ON THE COORDINATE now (OsmPinPatch, 2026-08-26) — the centred
        // pin is exact, not an approximation. The old single-tile version's own comment called
        // the centre-pin "an approximation"; measured, it was off by up to half a tile (~450 m)
        // and identically so for every card in the same tile, which the owner read — correctly —
        // as "all pins wrong and same". 📌 (vs 📍) still marks a geocoded APPROXIMATE location:
        // that distinction is about the COORDINATE's provenance and survives unchanged.
        <>
          {/* ⚠️ absolute inset-0, for the SAME reason the photo branch above carries it — and
              this branch is where the omission actually bit (owner 2026-08-09: "let card have
              same size always not changed by image shape"). `h-full` is height:100%, which does
              not resolve against a parent whose height comes from FLEX STRETCH, so the img fell
              back to its INTRINSIC height. An OSM tile is a fixed 256×256 square, so the map
              slide made the box 256px tall and pushed the whole card ~80px taller than the same
              card showing a photo. Clicking through the carousel resized the card under the
              agent's cursor, and the two states were the same listing.
              📌 The photo branch was fixed this way on 2026-07-30 and its comment states the
              rule; the map tile and the placeholder below were simply never brought along. When
              a rule is written next to one of three siblings, check the other two. */}
          {/* 0N: OsmPinPatch centres the WINDOW on the point, so the centred pin below is
              truthful — see the component's header for the wrong-and-same bug this replaced. */}
          <OsmPinPatch lat={coords.lat} lng={coords.lng} />
          {/* ⚠ the pin stays EMOJI on purpose (owner-caught regression 2026-07-20): this is
              a map MARKER, not a chip — its red color and 24-30px size ARE the signal on a
              busy tile. The sweep's 13px currentColor SVG was invisible. Same rule as the
              colored semaphores: when the glyph's own color carries the meaning, keep it.
              📍 precise coords / 📌 approximate (address- or district-level geocode). */}
          <span className={`absolute inset-0 flex items-center justify-center ${pinTextClass} drop-shadow`}>{hasPrecise ? '📍' : '📌'}</span>
        </>
        )
      ) : (
        /* absolute inset-0 for the same reason as the two branches above: a slide must FILL
           the box it is given and never state a height of its own. */
        <div className={`absolute inset-0 flex items-center justify-center ${pinTextClass} text-muted-foreground/50`}>
          {/* no-coords placeholder: muted monochrome is right for SVG, but it must scale
              with the container — the sweep left it at the 13px default. */}
          <Icon name="map" size={size === 'large' ? 30 : size === 'card' ? 24 : 18} />
        </div>
      )}
      {/* The explicit control, so zoom is discoverable and reachable without knowing the photo
          is clickable. ⚠️ Clicking the photo toggles zoom too — that REPLACES click-to-advance,
          which is only safe because the modal's tile strip now shows every photo, so the hero
          is no longer the way to reach photo 8. Every surface without `zoomable` is untouched. */}
      {canZoom && (
        <button type="button" title={t(zoom ? 'desk_gallery_zoom_out' : 'desk_gallery_zoom_in')}
          onClick={e => { e.stopPropagation(); setZoom(z => !z) }}
          className="absolute top-1 right-1 z-20 w-7 h-7 rounded-full border border-white/35 bg-black/60
                     text-white text-base leading-none flex items-center justify-center">
          {zoom ? '−' : '+'}
        </button>
      )}
      {slideCount > 1 && (
        <span className={`${OVERLAY_BADGE} bottom-1 right-1 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity`}>
          {i + 1}/{slideCount}
        </span>
      )}
    </div>
  )
}
